import { useRef, useState } from 'react';
import { Row, Col, Typography, Card, Button, Modal, Space, notification } from 'antd';
import { RocketOutlined, UsergroupAddOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { createAGroup } from '@/api/crm/groups';
import CRMGroupCreator, { CRMGroupCreatorRef } from './crmGroupCreator';
import { GroupData } from '@/types/TCRM';

const { Title, Text, Paragraph } = Typography;

const EmptyBoard: React.FC<{ clientID: string; clientData: any; refetchCRMGroups: () => void }> = ({
	clientID,
	clientData,
	refetchCRMGroups,
}) => {
	const formRef = useRef<CRMGroupCreatorRef>(null);
	const [isBoardModalOpen, setIsBoardModalOpen] = useState(false);

	const createGroupMutation = useMutation({
		mutationFn: createAGroup,
		onSuccess: () => {
			notification.success({
				message: 'Group Created',
				description: 'Your new CRM group has been successfully created.',
			});
			refetchCRMGroups();
			setIsBoardModalOpen(false);
			formRef.current?.resetForm();
		},
	});

	const handleSubmitForm = (values: GroupData) => {
		createGroupMutation.mutate({ clientID, groupData: values });
	};

	return (
		<div className="bg-white min-h-screen p-8 flex flex-col items-center justify-start">
			<div className="text-center max-w-2xl mb-8">
				<Title
					level={1}
					className="text-2xl mb-4 text-gray-800 flex items-center justify-center gap-3"
				>
					<RocketOutlined className="text-blue-500" />
					Get Started with Your CRM
				</Title>

				<Paragraph className="text-base text-gray-600 mb-6">
					Create groups to organize your leads and track their progress. Your AI receptionist will
					automatically add new leads to the right groups.
				</Paragraph>
			</div>

			<Row gutter={[24, 24]} className="max-w-3xl w-full">
				<Col span={24}>
					<Card className="text-center p-6 shadow-sm hover:shadow-md transition-shadow">
						<Space direction="vertical" size="large" className="w-full">
							<div className="text-6xl text-blue-500 flex justify-center">
								<UsergroupAddOutlined />
							</div>
							<Title level={3}>Create Your First Group</Title>
							<Text className="text-gray-600">
								Start by creating a group like "New Leads" or "Interested Customers" to organize
								your contacts.
							</Text>
							<Button
								type="primary"
								size="large"
								onClick={() => setIsBoardModalOpen(true)}
								className="mt-4"
								icon={<PlusOutlined />}
							>
								Create Group
							</Button>
						</Space>
					</Card>
				</Col>
			</Row>

			<Modal
				open={isBoardModalOpen}
				onCancel={() => setIsBoardModalOpen(false)}
				footer={null}
				width={800}
				centered
			>
				<CRMGroupCreator
					ref={formRef}
					onSubmit={handleSubmitForm}
					isLoading={createGroupMutation.isPending}
					clientData={clientData}
				/>
			</Modal>
		</div>
	);
};

export default EmptyBoard;
